import { AlertCircle, Eye, Lock, Mail, Scissors, useEffect, useState } from "../core/vendor.js";
import { api } from "../core/api.js";
import { COLORS } from "../core/config.jsx";
import { MOCK_USERS } from "../core/demo-data.js";
import { Btn } from "../core/ui.jsx";
import { SubscriptionsPage } from "../finance/subscriptions.jsx";


// ============================================================
// COMPONENTS
// ============================================================

// --- Sidebar ---
// ============================================================
// COOKIE BANNER (DSGVO / TTDSG)
// ============================================================
export const CookieBanner = () => {
  const [visible, setVisible] = useState(() => {
    try { return !localStorage.getItem("shearly_cookie_ok"); } catch { return true; }
  });
  if (!visible) return null;
  const accept = () => {
    try { localStorage.setItem("shearly_cookie_ok", "essential"); } catch {}
    setVisible(false);
  };
  return (
    <div style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 9999,
      background: "rgba(31,41,55,0.97)", backdropFilter: "blur(8px)",
      padding: "14px 24px", display: "flex", alignItems: "center", justifyContent: "center",
      gap: 16, flexWrap: "wrap", fontSize: 13, color: "#E5E7EB",
    }}>
      <Lock size={14} style={{ flexShrink: 0, opacity: 0.7 }} />
      <span style={{ maxWidth: 600 }}>
        Diese Website verwendet ausschließlich <strong style={{ color: "#fff" }}>technisch notwendige Cookies</strong> für die Authentifizierung. Keine Tracking- oder Marketing-Cookies.{" "}
        <a href="#" onClick={e => { e.preventDefault(); }} style={{ color: "#A78BFA", textDecoration: "underline" }}>Datenschutzerklärung</a>
      </span>
      <button onClick={accept} style={{
        padding: "8px 20px", borderRadius: 8, border: "none", background: "#7C3AED", color: "#fff",
        fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap", flexShrink: 0,
      }}>Verstanden</button>
    </div>
  );
};

// ============================================================
// LOGIN SCREEN
// ============================================================
// TERMS_ACCEPTANCE_v1 -- Click-Wrap-Modal. Blockt die App nach Login bis User AGB/AVV/Datenschutz akzeptiert.
// Pflicht-Gate. Logout-Option vorhanden falls User nicht zustimmen will.
export const TermsAcceptanceGate = ({ currentUser, onAccepted, onLogout }) => {
  const [checkAgb, setCheckAgb] = useState(false);
  const [checkAvv, setCheckAvv] = useState(false);
  const [checkDsgvo, setCheckDsgvo] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const allChecked = checkAgb && checkAvv && checkDsgvo;
  const version = currentUser.current_terms_version || "—";

  const submit = async () => {
    if (!allChecked) return;
    setSubmitting(true);
    setErr("");
    try {
      const r = await api.request("/auth/accept-terms", {
        method: "POST",
        body: JSON.stringify({ confirm: true, accepted_version: version }),
      });
      if (r && r.ok) {
        onAccepted({ ...currentUser, terms_acceptance_required: false, terms_accepted_version: version });
      } else {
        setErr((r && r.data && r.data.error) || "Speichern fehlgeschlagen.");
        setSubmitting(false);
      }
    } catch (e) {
      setErr("Netzwerk-Fehler — bitte erneut versuchen.");
      setSubmitting(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 100000,
      background: "linear-gradient(135deg, #1E1B4B 0%, #312E81 50%, #4C1D95 100%)",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: 20, overflow: "auto"
    }}>
      <div style={{
        maxWidth: 640, width: "100%", background: "#fff", borderRadius: 16,
        boxShadow: "0 25px 50px -12px rgba(0,0,0,0.5)", overflow: "hidden"
      }}>
        <div style={{
          background: "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)",
          color: "#fff", padding: "24px 28px"
        }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, opacity: 0.85, textTransform: "uppercase", marginBottom: 6 }}>
            Einmalige Bestätigung
          </div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, lineHeight: 1.2 }}>
            Nutzungsbedingungen akzeptieren
          </h2>
          <div style={{ fontSize: 13, marginTop: 8, opacity: 0.92 }}>
            Hallo {currentUser.name || "und willkommen"} — bevor du Shearly nutzen kannst, lies und bestätige bitte unsere
            rechtlichen Dokumente. Du machst das nur einmal (oder erneut, wenn wir etwas ändern).
          </div>
        </div>

        <div style={{ padding: "20px 28px", color: "#1F2937" }}>
          <div style={{ fontSize: 13, lineHeight: 1.55, marginBottom: 16, color: "#4B5563" }}>
            Mit der Nutzung von Shearly stimmst du den folgenden drei Dokumenten zu. Du kannst sie jederzeit in voller Länge
            unter <i>Datenschutz / AGB / AVV</i> im linken Menü nachlesen.
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 }}>
            <label style={{
              display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px",
              border: "1.5px solid " + (checkAgb ? "#10B981" : "#E5E7EB"), borderRadius: 10,
              cursor: "pointer", background: checkAgb ? "rgba(16,185,129,0.06)" : "#fff", transition: "all 0.15s"
            }}>
              <input type="checkbox" checked={checkAgb} onChange={e => setCheckAgb(e.target.checked)}
                style={{ marginTop: 3, accentColor: "#10B981", width: 18, height: 18, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Allgemeine Geschäftsbedingungen (AGB)</div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                  Vertragliche Grundlage der Software-Nutzung — Laufzeit, Kündigung, Preise, Pflichten.
                </div>
              </div>
            </label>

            <label style={{
              display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px",
              border: "1.5px solid " + (checkAvv ? "#10B981" : "#E5E7EB"), borderRadius: 10,
              cursor: "pointer", background: checkAvv ? "rgba(16,185,129,0.06)" : "#fff", transition: "all 0.15s"
            }}>
              <input type="checkbox" checked={checkAvv} onChange={e => setCheckAvv(e.target.checked)}
                style={{ marginTop: 3, accentColor: "#10B981", width: 18, height: 18, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Auftragsverarbeitungsvertrag (AVV)</div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                  DSGVO-Pflicht gemäß Art. 28 — du bist Verantwortlicher, Shearly ist Auftragsverarbeiter.
                  Du bestätigst u.a., dass du Einwilligungen deiner Endkunden selbst rechtmäßig dokumentierst.
                </div>
              </div>
            </label>

            <label style={{
              display: "flex", alignItems: "flex-start", gap: 10, padding: "12px 14px",
              border: "1.5px solid " + (checkDsgvo ? "#10B981" : "#E5E7EB"), borderRadius: 10,
              cursor: "pointer", background: checkDsgvo ? "rgba(16,185,129,0.06)" : "#fff", transition: "all 0.15s"
            }}>
              <input type="checkbox" checked={checkDsgvo} onChange={e => setCheckDsgvo(e.target.checked)}
                style={{ marginTop: 3, accentColor: "#10B981", width: 18, height: 18, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Datenschutzerklärung</div>
                <div style={{ fontSize: 12, color: "#6B7280", marginTop: 2 }}>
                  Welche Daten Shearly speichert und wie sie verarbeitet werden — gemäß Art. 13/14 DSGVO.
                </div>
              </div>
            </label>
          </div>

          <div style={{
            background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: 12, marginBottom: 16, fontSize: 11.5, color: "#6B7280", lineHeight: 1.55
          }}>
            <b>Was bei Bestätigung passiert:</b> Wir speichern Datum, Uhrzeit, deine IP-Adresse und die Version
            der akzeptierten Dokumente ({version}) im Audit-Log. Diese Bestätigung ist rechtlich verbindlich.
            Du kannst deine Bestätigung jederzeit in deinem Profil einsehen.
          </div>

          {err && (
            <div style={{
              background: "rgba(239,68,68,0.10)", border: "1px solid rgba(239,68,68,0.4)",
              borderRadius: 8, padding: 10, marginBottom: 12, fontSize: 12, color: "#991B1B"
            }}>{err}</div>
          )}

          <div style={{ display: "flex", gap: 10, justifyContent: "space-between", alignItems: "center" }}>
            <button
              onClick={onLogout}
              disabled={submitting}
              style={{
                padding: "10px 14px", border: "1px solid #D1D5DB", borderRadius: 8,
                background: "transparent", color: "#6B7280", fontSize: 12, fontWeight: 600,
                cursor: submitting ? "wait" : "pointer"
              }}>
              Nicht zustimmen — abmelden
            </button>
            <button
              onClick={submit}
              disabled={!allChecked || submitting}
              style={{
                padding: "12px 24px", border: "none", borderRadius: 8,
                background: allChecked ? "linear-gradient(135deg, #7C3AED 0%, #A78BFA 100%)" : "#D1D5DB",
                color: "#fff", fontSize: 14, fontWeight: 700,
                cursor: (allChecked && !submitting) ? "pointer" : "not-allowed",
                boxShadow: allChecked ? "0 4px 12px rgba(124,58,237,0.4)" : "none",
                transition: "all 0.2s"
              }}>
              {submitting ? "Speichere…" : "✓ Akzeptieren & weiter"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* TRIAL_LOCK_v1 -- Sanfte Sperre (Trial abgelaufen + Kulanz vorbei ODER Salon pausiert).
   Inhaber: kommt rein, sieht NUR die Abo-Seite ("Plan wählen, dann geht's sofort weiter")
   — der Bezahlweg bleibt offen. Mitarbeiter/andere Rollen: Hinweis + Abmelden.
   "Freischaltung prüfen" ruft /api/stripe/activate (stellt plan/status nach Stripe-Abo um,
   es gibt keinen Webhook dafür) und lädt danach /auth/me neu — Entsperren wirkt sofort. */
export const SalonLockGate = ({ currentUser, onLogout, onUnlocked }) => {
  const [checking, setChecking] = useState(false);
  const [checkMsg, setCheckMsg] = useState("");
  // PAYMENT_CONFIRM_UX_v1 -- direkt nach Rückkehr von Stripe (?payment=success):
  // freundliche "wird bestätigt"-Ansicht statt rotem Sperrbildschirm.
  const [confirming, setConfirming] = useState(() => {
    try { return currentUser.role === "owner" && window.location.search.indexOf("payment=success") !== -1; }
    catch (e) { return false; }
  });
  const lock = currentUser.salon_lock || {};
  const isOwner = currentUser.role === "owner";
  const isSuspended = lock.reason === "suspended" || lock.reason === "cancelled";

  const recheck = async (viaActivate) => {
    setChecking(true);
    setCheckMsg("");
    try {
      if (viaActivate) {
        const act = await api.request("/stripe/activate", { method: "POST", body: "{}" });
        if (act && act.ok && act.data && act.data.success === false && act.data.message) {
          setCheckMsg(act.data.message);
        }
      }
      const me = await api.request("/auth/me");
      if (me && me.ok && me.data && me.data.id) {
        if (!me.data.salon_lock || me.data.salon_lock.state !== "locked") {
          onUnlocked(me.data);  // App rendert wieder normal + lädt Daten
          return;
        }
        if (!viaActivate) setCheckMsg("Noch gesperrt — Zahlung wurde noch nicht gefunden.");
      }
    } catch (e) {
      setCheckMsg("Verbindungsfehler — bitte erneut versuchen.");
    }
    setChecking(false);
  };

  // PAYMENT_CONFIRM_UX_v1 -- Rückkehr von Stripe: automatisch + wiederholt freischalten,
  // bis Stripe das Abo bestätigt (kein Webhook -> Pollen). Bis dahin "wird bestätigt"-Ansicht.
  useEffect(() => {
    if (!confirming) return;
    try { window.history.replaceState({}, "", window.location.pathname); } catch (e) {}
    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 30; // ~2,5 Min bei 5s-Takt
    const poll = async () => {
      if (cancelled) return;
      attempts++;
      try {
        await api.request("/stripe/activate", { method: "POST", body: "{}" });
        const me = await api.request("/auth/me");
        if (cancelled) return;
        if (me && me.ok && me.data && me.data.id &&
            (!me.data.salon_lock || me.data.salon_lock.state !== "locked")) {
          onUnlocked(me.data); // entsperrt -> App lädt normal + zeigt Termine wieder
          return;
        }
      } catch (e) {}
      if (cancelled) return;
      if (attempts >= MAX_ATTEMPTS) {
        setConfirming(false); // Fallback: normale Ansicht mit manuellem Freischaltungs-Knopf
        setCheckMsg("Die Bestätigung dauert länger als sonst. Bitte unten auf den Knopf zum Prüfen der Freischaltung tippen.");
        return;
      }
      setTimeout(poll, 5000);
    };
    poll();
    return () => { cancelled = true; };
  }, [confirming]);

  const headline = isSuspended ? "Dein Zugang ist pausiert" : "Dein Testzeitraum ist abgelaufen";
  const sub = isSuspended
    ? "Dein Shearly-Zugang wurde vorübergehend pausiert. Bitte melde dich beim Shearly-Support (support@shearly.de), um ihn wieder zu aktivieren."
    : "Keine Sorge: Alle deine Daten, Termine und Einstellungen sind vollständig erhalten. Wähle einen Plan — danach geht es sofort weiter, genau dort, wo du aufgehört hast.";

  // PAYMENT_CONFIRM_UX_v1 -- freundliche Bestätigungs-Ansicht direkt nach dem Bezahlen
  // (statt des roten Sperrbildschirms), während im Hintergrund automatisch freigeschaltet wird.
  if (confirming) {
    return (
      <div style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "24px 16px" }}>
        <div style={{ maxWidth: 540, width: "100%", textAlign: "center", background: "var(--card)", border: `1.5px solid ${COLORS.border}`, borderRadius: 16, padding: "36px 28px", boxShadow: "0 8px 30px rgba(0,0,0,0.12)" }}>
          <div style={{ fontSize: 46, marginBottom: 10 }}>✅</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, marginBottom: 12 }}>
            Zahlung erhalten — wird gerade bestätigt …
          </div>
          <div style={{ fontSize: 14.5, lineHeight: 1.65, color: COLORS.text, opacity: 0.9 }}>
            Einen kleinen Moment, wir schalten deinen Zugang frei. Nach dem Bezahlen kann das bis zu einer Minute dauern.
          </div>
          <div style={{ marginTop: 16, padding: "12px 16px", background: "rgba(16,185,129,0.10)", border: "1px solid rgba(16,185,129,0.35)", borderRadius: 10, fontSize: 13.5, lineHeight: 1.6, color: COLORS.text }}>
            <strong>Alle deine Termine, Kunden und Einstellungen sind sicher.</strong> Sobald die Bestätigung da ist, geht es <strong>automatisch</strong> weiter — genau dort, wo du aufgehört hast.
          </div>
          <div style={{ marginTop: 18, fontSize: 12.5, color: COLORS.warning, fontWeight: 600 }}>
            ⏳ Bitte diese Seite geöffnet lassen.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: "100vh", background: COLORS.bg, display: "flex", flexDirection: "column" }}>
      <div style={{
        background: "linear-gradient(90deg, #DC2626, #B91C1C)", color: "#fff",
        padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "center",
        gap: 12, flexWrap: "wrap", textAlign: "center", boxShadow: "0 2px 12px rgba(220,38,38,0.4)"
      }}>
        <span style={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.3 }}>🔒 {headline}</span>
        <button onClick={onLogout} style={{
          background: "rgba(255,255,255,0.92)", color: "#991B1B", border: "none", borderRadius: 6,
          padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 800
        }}>Abmelden</button>
      </div>
      <div style={{ flex: 1, overflow: "auto", padding: "24px 16px" }}>
        <div style={{ maxWidth: 880, margin: "0 auto" }}>
          <div style={{
            background: "var(--card)", border: `1.5px solid ${COLORS.border}`, borderRadius: 14,
            padding: "20px 24px", marginBottom: 20, fontSize: 14, lineHeight: 1.6, color: COLORS.text
          }}>
            {sub}
            {isOwner && !isSuspended && (
              <div style={{ marginTop: 14, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                <Btn size="sm" variant="secondary" disabled={checking} onClick={() => recheck(true)}>
                  {checking ? "Prüfe…" : "Ich habe bereits bezahlt — Freischaltung prüfen"}
                </Btn>
                {checkMsg && <span style={{ fontSize: 12, color: COLORS.warning, fontWeight: 600 }}>{checkMsg}</span>}
              </div>
            )}
          </div>
          {isOwner && !isSuspended
            ? <SubscriptionsPage currentUser={currentUser} />
            : (!isOwner && (
              <div style={{
                background: "var(--card)", border: `1.5px solid ${COLORS.border}`, borderRadius: 14,
                padding: "20px 24px", fontSize: 14, lineHeight: 1.6, color: COLORS.text
              }}>
                Der Zugang für deinen Salon ist derzeit pausiert. Bitte wende dich an deine
                Salon-Inhaberin / deinen Salon-Inhaber — sobald das Abo aktiv ist, kannst du
                dich wie gewohnt anmelden.
              </div>
            ))
          }
        </div>
      </div>
    </div>
  );
};

// COLOR_THEME_v1 -- LoginScreen bleibt fix Lila (Brand vor Login)
export const _LOGIN_PURPLE = { primary: "#7C3AED", primaryDark: "#5B21B6", primaryLight: "#A78BFA" };
export const LoginScreen = ({ onLogin, onDemoLogin }) => {
  // Lokaler COLORS-Override: alle Akzent-Farben werden Lila in der LoginScreen
  const COLORS = Object.assign({}, window.__SHEARLY_OUTER_COLORS__ || {}, _LOGIN_PURPLE);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
    const [connecting, setConnecting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // PERSIST_LOGIN_v1 (5.2 leichte Version) -- "Angemeldet bleiben"
  const [remember, setRemember] = useState(() => { try { return localStorage.getItem("sp_remember") === "1"; } catch { return false; } });
  const [showDemoField, setShowDemoField] = useState(false);
  const [demoCode, setDemoCode] = useState("");
  const [demoError, setDemoError] = useState("");

  const [loading, setLoading] = useState(false);
    const [forgotMode, setForgotMode] = React.useState(false);
    const [forgotEmail, setForgotEmail] = React.useState("");
    const [forgotMsg, setForgotMsg] = React.useState("");
    const [forgotLoading, setForgotLoading] = React.useState(false);
    const [resetToken, setResetToken] = React.useState(() => { const p = new URLSearchParams(window.location.search); return p.get("reset") || null; });
    const [resetPw, setResetPw] = React.useState("");
    const [resetPw2, setResetPw2] = React.useState("");
    const [resetMsg, setResetMsg] = React.useState("");
    const [showResetPw, setShowResetPw] = React.useState(false);

    React.useEffect(() => {
      const h = window.location.hash;
      if (h && h.startsWith("#reset=")) {
        setResetToken(h.replace("#reset=", ""));
      }
    }, []);

    const handleForgot = () => {
      setForgotLoading(true);
      setForgotMsg("");
      fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({email: forgotEmail})
      }).then(r => r.json()).then(d => {
        setForgotMsg(d.message || "Reset-Link wurde gesendet.");
        setForgotLoading(false);
      }).catch(() => {
        setForgotMsg("Verbindungsfehler");
        setForgotLoading(false);
      });
    };

    const handleReset = () => {
      if (resetPw !== resetPw2) { setResetMsg("Passwörter stimmen nicht überein"); return; }
      if (resetPw.length < 8) { setResetMsg("Mind. 8 Zeichen + 1 Zahl/Sonderzeichen"); return; }
      setForgotLoading(true);
      fetch("/api/auth/reset-password", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({token: resetToken, password: resetPw})
      }).then(r => r.json()).then(d => {
        if (d.success) {
          setResetMsg("Passwort geändert! Du kannst dich jetzt anmelden.");
          setResetToken(null);
          window.location.hash = "";
          setTimeout(() => { setForgotMode(false); setResetMsg(""); }, 3000);
        } else {
          setResetMsg(d.error || "Fehler");
        }
        setForgotLoading(false);
      }).catch(() => {
        setResetMsg("Verbindungsfehler");
        setForgotLoading(false);
      });
    };


  const handleLogin = async () => {
    setLoading(true);
    setError("");
    // PERSIST_LOGIN_v1 (5.2): Remember-Flag VOR setTokens, damit der richtige Storage verwendet wird
    api.setRemember(remember);
    // Try real API first
    const res = await api.login(email, password);
    if (res.ok) {
      api.setTokens(res.data.access_token, res.data.refresh_token);
      setLoading(false);
      onLogin(res.data.user);
      return;
    }
    // Fallback to mock if backend is offline
    if (res.offline) {
      const user = MOCK_USERS.find(u => u.email === email && u.password === password);
      if (user) { setLoading(false); onLogin(user); return; }
    }
    setLoading(false);
    setError(res.data?.error || "E-Mail oder Passwort falsch.");
  };

  return (

      resetToken ? (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)" }}>
          <div style={{ background: "var(--card)", borderRadius: 16, padding: "40px 36px", width: 400, maxWidth: "90vw", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Scissors size={28} color={COLORS.primary} />
              <h2 style={{ color: COLORS.primary, margin: "8px 0 4px" }}>Shearly</h2>
              <p style={{ color: COLORS.textLight, fontSize: 14 }}>Neues Passwort setzen</p>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Neues Passwort</label>
              <div style={{ position: "relative" }}>
                <input type={showResetPw ? "text" : "password"} value={resetPw} onChange={e => setResetPw(e.target.value)}
                  placeholder="Mind. 8 Zeichen + 1 Zahl/Sonderzeichen"
                  style={{ width: "100%", padding: "10px 40px 10px 12px", border: "1px solid " + COLORS.border, borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />
                <button onClick={() => setShowResetPw(!showResetPw)} style={{ position: "absolute", right: 8, top: 8, background: "none", border: "none", cursor: "pointer", color: COLORS.textLight }}>
                  <Eye size={16} />
                </button>
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>Passwort bestätigen</label>
              <input type="password" value={resetPw2} onChange={e => setResetPw2(e.target.value)}
                placeholder="Passwort wiederholen"
                style={{ width: "100%", padding: "10px 12px", border: "1px solid " + COLORS.border, borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            {resetMsg && <p style={{ fontSize: 13, color: resetMsg.includes("geändert") ? "#059669" : COLORS.danger, textAlign: "center", marginBottom: 12 }}>{resetMsg}</p>}
            <button onClick={handleReset} disabled={forgotLoading} style={{
              width: "100%", padding: 12, background: COLORS.primary, color: "white", border: "none",
              borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer"
            }}>{forgotLoading ? "Wird gespeichert..." : "Passwort speichern"}</button>
          </div>
        </div>
      ) :
      forgotMode ? (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #7c3aed 0%, #a78bfa 100%)" }}>
          <div style={{ background: "var(--card)", borderRadius: 16, padding: "40px 36px", width: 400, maxWidth: "90vw", boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <Scissors size={28} color={COLORS.primary} />
              <h2 style={{ color: COLORS.primary, margin: "8px 0 4px" }}>Shearly</h2>
              <p style={{ color: COLORS.textLight, fontSize: 14 }}>Passwort zurücksetzen</p>
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: "block", marginBottom: 6 }}>E-Mail</label>
              <input type="email" value={forgotEmail} onChange={e => setForgotEmail(e.target.value)}
                placeholder="Deine E-Mail-Adresse"
                onKeyDown={e => { if (e.key === "Enter") handleForgot(); }}
                style={{ width: "100%", padding: "10px 12px", border: "1px solid " + COLORS.border, borderRadius: 8, fontSize: 14, boxSizing: "border-box" }} />
            </div>
            {forgotMsg && <p style={{ fontSize: 13, color: forgotMsg.includes("gesendet") ? "#059669" : COLORS.danger, textAlign: "center", marginBottom: 12 }}>{forgotMsg}</p>}
            <button onClick={handleForgot} disabled={forgotLoading} style={{
              width: "100%", padding: 12, background: COLORS.primary, color: "white", border: "none",
              borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer", marginBottom: 16
            }}>{forgotLoading ? "Wird gesendet..." : "Reset-Link senden"}</button>
            <div style={{ textAlign: "center" }}>
              <a href="#" onClick={(e) => { e.preventDefault(); setForgotMode(false); setForgotMsg(""); }} style={{ color: COLORS.primary, fontSize: 14, textDecoration: "none" }}>Zurück zum Login</a>
            </div>
          </div>
        </div>
      ) :


    <div style={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      // COLOR_THEME_v1 -- Login-Page bleibt fix Lila (Brand-Identität vor Login, Theme-Switch ist erst danach zugänglich)
      background: "linear-gradient(135deg, #5B21B6 0%, #7C3AED 40%, #A78BFA 100%)",
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* COLOR_THEME_v1 -- Login-Page bleibt komplett Lila (Brand) */}
      {(() => null)()}
      <div style={{
        background: "var(--card)", borderRadius: 16, padding: "40px 24px", width: "100%", maxWidth: 400, boxShadow: "0 20px 60px rgba(0,0,0,0.3)", margin: "0 16px",
      }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Scissors size={32} color="#7C3AED" />
            {/* BRAND_TAG_v1 -- Produktkuerzel auch auf der Login-Seite */}
            <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
              <span style={{ fontSize: 28, fontWeight: 800, color: COLORS.primary }}>Shearly</span>
              <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase", color: COLORS.primary, border: `1px solid ${COLORS.primary}`, padding: "2px 7px", borderRadius: 6, lineHeight: "14px" }}>Pro</span>
            </span>
          </div>
          <p style={{ color: COLORS.textLight, fontSize: 14, margin: 0 }}>Melde dich an um fortzufahren</p>
        </div>

        {error && (
          <div style={{ padding: "10px 14px", background: `${COLORS.danger}10`, border: `1px solid ${COLORS.danger}30`, borderRadius: 8, marginBottom: 16, fontSize: 13, color: COLORS.danger, display: "flex", alignItems: "center", gap: 8 }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: COLORS.text }}>E-Mail</label>
          <div style={{ position: "relative" }}>
            <Mail size={16} style={{ position: "absolute", left: 12, top: 12, color: COLORS.textLight }} />
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              placeholder="deine@email.de"
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              style={{ width: "100%", padding: "10px 10px 10px 38px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: "none", boxSizing: "border-box" }}
            />
          </div>
        </div>

        <div style={{ marginBottom: 24 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 6, color: COLORS.text }}>Passwort</label>
          <div style={{ position: "relative" }}>
            <Lock size={16} style={{ position: "absolute", left: 12, top: 12, color: COLORS.textLight }} />
            <input type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              onKeyDown={e => e.key === "Enter" && handleLogin()}
              style={{ width: "100%", padding: "10px 38px 10px 38px", borderRadius: 8, border: `1px solid ${COLORS.border}`, fontSize: 14, outline: "none", boxSizing: "border-box" }}
            />
            <button onClick={() => setShowPassword(!showPassword)} style={{ position: "absolute", right: 8, top: 8, background: "none", border: "none", cursor: "pointer", color: COLORS.textLight }}>
              <Eye size={16} />
            </button>
          </div>
        </div>

        {/* PERSIST_LOGIN_v1 (5.2 leichte Version) */}
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: COLORS.textLight, cursor: "pointer", marginBottom: 14, userSelect: "none" }}>
          <input type="checkbox" checked={remember} onChange={e => setRemember(e.target.checked)} style={{ width: 16, height: 16, cursor: "pointer", accentColor: COLORS.primary }} />
          Angemeldet bleiben
          <span style={{ fontSize: 11, opacity: 0.7 }}>· nur auf eigenem Gerät</span>
        </label>

        <button onClick={handleLogin} style={{
          width: "100%", padding: "12px 0", background: COLORS.primary, color: "white", border: "none",
          borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: "pointer", marginBottom: 16,
          transition: "background 0.15s"
        }}
          onMouseEnter={e => e.currentTarget.style.background = COLORS.primaryDark}
          onMouseLeave={e => e.currentTarget.style.background = COLORS.primary}
          disabled={loading}
        >
          {loading ? "Wird angemeldet..." : "Anmelden"}
        </button>

        {/* TERMS_ACCEPTANCE_v1 -- Hinweis-Text. Eigentlicher Click-Wrap erfolgt nach Login im Gate. */}
        <div style={{ textAlign: "center", fontSize: 10.5, color: COLORS.textLight, marginTop: 10, lineHeight: 1.5 }}>
          Mit der Anmeldung erkennst du unsere{" "}
          <span style={{ color: COLORS.primary, fontWeight: 600 }}>AGB</span>,{" "}
          <span style={{ color: COLORS.primary, fontWeight: 600 }}>Datenschutzerklärung</span> und den{" "}
          <span style={{ color: COLORS.primary, fontWeight: 600 }}>Auftragsverarbeitungsvertrag</span> an.<br/>
          Bei deiner ersten Anmeldung bitten wir um eine zusätzliche Bestätigung.
        </div>

        <div style={{ textAlign: "center", fontSize: 12, color: COLORS.textLight, marginTop: 12 }}>
          <a href="#" onClick={(e) => { e.preventDefault(); setForgotMode(true); }} style={{ color: COLORS.primary, textDecoration: "none" }}>Passwort vergessen?</a>
        </div>

        <div style={{ marginTop: 20, paddingTop: 20, borderTop: `1px solid ${COLORS.border}`, textAlign: "center" }}>
          <p style={{ fontSize: 12, color: COLORS.textLight, margin: "0 0 12px" }}>Noch kein Konto? Schauen Sie sich Shearly unverbindlich an:</p>
          {!showDemoField ? (
            <button onClick={() => { setShowDemoField(true); setDemoError(""); }} style={{
              width: "100%", padding: "12px 0", background: "transparent", color: COLORS.primary,
              border: `2px solid ${COLORS.primary}`, borderRadius: 8, fontSize: 14, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              transition: "all 0.15s"
            }}
              onMouseEnter={e => { e.currentTarget.style.background = COLORS.primary; e.currentTarget.style.color = "white"; }}
              onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = COLORS.primary; }}
            >
              <Eye size={16} /> Demo ansehen
            </button>
          ) : (
            <div>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  placeholder="Demo-Code eingeben"
                  value={demoCode}
                  onChange={e => { setDemoCode(e.target.value); setDemoError(""); }}
                  onKeyDown={e => { if (e.key === "Enter") { if (demoCode === "SHEARLY.2014") onDemoLogin(); else setDemoError("Ungültiger Code"); } }}
                  /* DEMO_NO_AUTOFILL_v1 -- verhindert dass Browser den Demo-Code automatisch ausfüllt */
                  autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
                  name="shearly-demo-code-no-autofill"
                  data-1p-ignore data-lpignore="true" data-form-type="other"
                  style={{
                    flex: 1, padding: "10px 12px", border: `1.5px solid ${demoError ? COLORS.danger : COLORS.border}`,
                    borderRadius: 8, fontSize: 14, outline: "none", transition: "border 0.15s",
                  }}
                  autoFocus
                />
                <button onClick={() => { if (demoCode === "SHEARLY.2014") onDemoLogin(); else setDemoError("Ungültiger Code"); }} style={{
                  padding: "10px 16px", background: COLORS.primary, color: "white", border: "none",
                  borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap",
                }}>
                  Starten
                </button>
              </div>
              {demoError && <p style={{ color: COLORS.danger, fontSize: 12, margin: "8px 0 0", fontWeight: 600 }}>{demoError}</p>}
            </div>
          )}
        </div>
      </div>
      <CookieBanner />
    </div>
  );
};
